import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, User, MapPin, Calendar, Award } from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import graffitiExample1 from "@assets/17549603541495383433150745869252_1754960370584.jpg";
import graffitiExample2 from "@assets/17549604632544130851901426668773_1754960472875.jpg";
import graffitiExample3 from "@assets/17549605081246944257471877272903_1754960515553.jpg";
import sirumArtwork from "@assets/17549610971494245148306314066316_1754961142862.jpg";
import linzArtwork from "@assets/17549611944126061631482328133336_1754961208430.jpg";

interface GraffitiArtist {
  id: string;
  name: string;
  aka: string;
  city: string;
  country: string;
  activeYears: string;
  style: string;
  biography: string;
  achievements: string[];
  ironlakAmbassador: boolean;
  profileImageUrl?: string;
}

export default function Artists() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: artists = [], isLoading } = useQuery({
    queryKey: ["/api/artists"],
  });

  const filteredArtists = (artists as GraffitiArtist[]).filter((artist: GraffitiArtist) => 
    artist.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    artist.aka?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    artist.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-base flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-neon-green border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white">Loading Artist Biographies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-base text-white">
      {/* Header */}
      <header className="bg-dark-elevated border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/")}
              className="text-neon-green"
              data-testid="button-back"
            >
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center space-x-2">
              <User className="text-coral" size={24} />
              <h1 className="text-xl font-bold">Australian Graffiti Artists</h1>
            </div>
          </div>
          <div className="text-sm text-gray-400">
            {(artists as GraffitiArtist[]).length} Artists Featured
          </div>
        </div>
      </header>

      <div className="p-6 max-w-6xl mx-auto">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search artists by name or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-dark-elevated border-gray-600 text-white"
              data-testid="input-search-artists"
            />
          </div>
        </div>

        {/* Artist Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArtists.map((artist: GraffitiArtist) => (
            <Card 
              key={artist.id}
              className="bg-dark-elevated border-gray-600 hover:border-neon-green transition-colors"
              data-testid={`artist-card-${artist.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                      {artist.name}
                      <Award className="text-coral" size={16} />
                    </CardTitle>
                    <p className="text-neon-green font-medium">
                      aka "{artist.aka}"
                    </p>
                  </div>
                  <Badge className="bg-neon-green text-black">
                    Featured Artist
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Location and Years */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-1 text-gray-300">
                    <MapPin size={14} />
                    <span>{artist.city}, {artist.country}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-gray-300">
                    <Calendar size={14} />
                    <span>{artist.activeYears}</span>
                  </div>
                </div>

                {/* Style Badge */}
                <div>
                  <Badge variant="outline" className="border-neon-green text-neon-green">
                    {artist.style}
                  </Badge>
                </div>

                {/* Featured Artwork Gallery */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-neon-green">Featured Artwork</h3>
                  {(() => {
                    // Assign correct artwork to each artist based on actual images provided
                    let artworkImage = null;
                    let artworkDescription = "";
                    
                    if (artist.name?.includes('LINZ')) {
                      artworkImage = linzArtwork;
                      artworkDescription = "LINZ with his colorful wildstyle piece";
                    } else if (artist.name?.includes('SIRUM') || artist.aka?.includes('SIRUM')) {
                      artworkImage = sirumArtwork;
                      artworkDescription = "SIRUM orange & blue piece on concrete wall";
                    } else if (artist.name?.includes('REALS')) {
                      artworkImage = graffitiExample1;
                      artworkDescription = "Vibrant blue & green wildstyle piece";
                    } else if (artist.name?.includes('SOFLES')) {
                      artworkImage = graffitiExample2;
                      artworkDescription = "Yellow & green masterpiece on brick";
                    } else if (artist.name?.includes('TUES')) {
                      artworkImage = graffitiExample3;
                      artworkDescription = "Multi-color burner with purple & orange";
                    } else if (artist.name?.includes('ADNATE')) {
                      artworkImage = graffitiExample3;
                      artworkDescription = "Multi-color burner with purple & orange";
                    } else {
                      // Default to first image for other artists
                      artworkImage = graffitiExample1;
                      artworkDescription = "Street art masterpiece";
                    }
                    
                    return (
                      <div className="space-y-2">
                        <img 
                          src={artworkImage} 
                          alt={`${artist.name} graffiti artwork`}
                          className="w-full h-32 object-cover rounded-lg shadow-lg border border-gray-600"
                          data-testid={`artist-artwork-${artist.id}`}
                        />
                        <p className="text-xs text-gray-400 text-center">{artworkDescription}</p>
                      </div>
                    );
                  })()}
                  <div className="bg-gradient-to-r from-neon-green/20 to-coral/20 p-3 rounded-lg border border-neon-green/30">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-lg">🎨</span>
                      <span className="text-sm font-medium text-neon-green">Featured Street Artist</span>
                    </div>
                    <p className="text-xs text-gray-300">
                      Known for innovative graffiti techniques and bold street art styles. Discover their signature pieces around the city.
                    </p>
                  </div>
                </div>

                {/* Achievements */}
                {artist.achievements && artist.achievements.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-2">Notable Achievements:</h4>
                    <ul className="text-xs text-gray-400 space-y-1">
                      {artist.achievements.slice(0, 3).map((achievement, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-coral mr-2">•</span>
                          {achievement}
                        </li>
                      ))}
                      {artist.achievements.length > 3 && (
                        <li className="text-neon-green text-xs">
                          +{artist.achievements.length - 3} more achievements
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {filteredArtists.length === 0 && (
          <div className="text-center py-12">
            <User className="mx-auto text-gray-500 mb-4" size={48} />
            <h3 className="text-lg font-semibold mb-2">No Artists Found</h3>
            <p className="text-gray-400">
              Try adjusting your search terms
            </p>
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <Card className="bg-dark-elevated border-gray-600 max-w-3xl mx-auto">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold mb-3 text-neon-green">About This Collection</h3>
              <p className="text-gray-300 mb-4">
                Celebrating the pioneers and contemporary masters of Australian street art and graffiti culture. 
                These artists have shaped the urban landscape and continue to inspire new generations of creators.
              </p>
              <div className="flex justify-center space-x-4 flex-wrap gap-2">
                <Badge className="bg-coral text-white">Australian Icons</Badge>
                <Badge className="bg-neon-green text-black">Street Art Legends</Badge>
                <Badge className="bg-purple-600 text-white">Cultural Impact</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}